import {
  Button,
  Card,
  CardBody,
  Center,
  HStack,
  Icon,
  Text,
  VStack,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { Smartphone, Monitor, Send } from 'lucide-react';
import { useState, useCallback } from 'react';
import { useFlag } from '@duke-hq/flags/react';
import type { StudentType } from '../StudentDetailPage';
import { SendPushNotificationModal } from './SendPushNotificationModal';

interface StudentDevicesProps {
  student: Pick<StudentType, 'devices' | 'drn'>;
}

export function StudentDevices({ student }: StudentDevicesProps) {
  const devices = student.devices ?? [];
  const isSendTestPushEnabled = useFlag('SEND_TEST_PUSH_NOTIFICATION_FEATURE');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDevice, setSelectedDevice] = useState<{
    id: string;
    deviceToken: string;
    platform: string;
    deviceModel: string | null;
  } | null>(null);

  const handleSendNotification = useCallback(
    (device: { id: string; deviceToken: string; platform: string; deviceModel: string | null }) => {
      setSelectedDevice(device);
      onOpen();
    },
    [setSelectedDevice, onOpen],
  );

  if (devices.length === 0) {
    return (
      <Center py={8}>
        <VStack spacing={2}>
          <Smartphone size={48} color="var(--chakra-colors-fg-muted)" />
          <Text color="fg.muted">
            <Trans>No registered devices</Trans>
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <VStack align="stretch" spacing={4} p={4}>
        {devices.map((device) => (
          <Card key={device.id} variant="outline">
            <CardBody>
              <HStack spacing={4} align="start">
                <Icon
                  as={device.platform === 'ios' ? Smartphone : Monitor}
                  boxSize={6}
                  color="fg.muted"
                />
                <VStack align="start" flex={1} spacing={2}>
                  <HStack spacing={2} width="100%">
                    <Text fontWeight="semibold">{device.deviceModel ?? 'Unknown Device'}</Text>
                    <Badge colorScheme={device.isActive ? 'green' : 'gray'}>
                      {device.isActive ? <Trans>Active</Trans> : <Trans>Inactive</Trans>}
                    </Badge>
                    {isSendTestPushEnabled === true && (
                      <Button
                        size="sm"
                        leftIcon={<Send size={16} />}
                        onClick={() => handleSendNotification(device)}
                        ml="auto"
                      >
                        <Trans>Send Test</Trans>
                      </Button>
                    )}
                  </HStack>

                  <VStack align="start" spacing={1} fontSize="sm" color="fg.muted">
                    <HStack>
                      <Text fontWeight="medium">
                        <Trans>Platform:</Trans>
                      </Text>
                      <Text textTransform="capitalize">{device.platform}</Text>
                    </HStack>

                    {device.osVersion != null && device.osVersion !== '' && (
                      <HStack>
                        <Text fontWeight="medium">
                          <Trans>OS Version:</Trans>
                        </Text>
                        <Text>{device.osVersion}</Text>
                      </HStack>
                    )}

                    {device.appVersion != null && device.appVersion !== '' && (
                      <HStack>
                        <Text fontWeight="medium">
                          <Trans>App Version:</Trans>
                        </Text>
                        <Text>{device.appVersion}</Text>
                      </HStack>
                    )}

                    <HStack>
                      <Text fontWeight="medium">
                        <Trans>Last Seen:</Trans>
                      </Text>
                      <Text>{new Date(device.lastSeenAt).toLocaleString()}</Text>
                    </HStack>

                    <HStack>
                      <Text fontWeight="medium">
                        <Trans>Registered:</Trans>
                      </Text>
                      <Text>{new Date(device.createdAt).toLocaleString()}</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </VStack>

      {selectedDevice != null && student.drn != null && (
        <SendPushNotificationModal
          isOpen={isOpen}
          onClose={onClose}
          device={selectedDevice}
          userDrn={student.drn}
        />
      )}
    </>
  );
}
